import { useContext, useEffect, useState } from 'react';
import { Circles } from 'react-loading-icons';
import { Rating } from 'react-simple-star-rating';
import { ReactComponent as StarsEmpty } from '../../../assets/images/StarSVGEmpty.svg';
import { ReactComponent as StarsFilled } from '../../../assets/images/StarSVGFilled.svg';
import {
  getAllCategoryQuestions,
  getRatingsById,
} from '../../../util/DatabaseUtil';
import {
  avgRatingsByQuestionId,
  getAvgRecommendBoolean,
  visualRating,
} from '../../../util/EntertainmentUtil';
import RatingProgressBar from '../../rating/ProgressBar';
import { RatingContext } from '../RatingPageTabs';

export default function ProductSummary(props) {
  const [kqRating, setKqRating] = useState([]);
  const [questions, setQuestions] = useState([]);
  const { categoryInformation, id } = useContext(RatingContext);

  useEffect(() => {
    const fetchKqRatingById = async () => {
      try {
        const data = await getRatingsById(id, categoryInformation.ID);
        setKqRating(data);
      } catch (err) {
        console.error('Error Fetching KnowQuest Rating', err);
      }
    };

    const getQuestions = async () => {
      try {
        const response = await getAllCategoryQuestions(categoryInformation.ID);
        const questions = response.data;
        setQuestions(questions);
      } catch (err) {
        console.error('Error Fetching Category Questions', err);
      }
    };

    getQuestions();
    fetchKqRatingById();
  }, []);

  return (
    <>
      {kqRating && questions ? (
        <div className="mx-4 md:mx-10 lg:mx-20 my-10 bg-[#081325] rounded-3xl p-6 md:p-10 shadow-2xl border border-slate-800 text-white font-montserrat">
          <h3 className="text-2xl font-bold text-slate-100 mb-6 border-b border-slate-800 pb-4">
            KnowQuest Rating
          </h3>

          <div className="flex justify-center mb-10 bg-[#111827] border border-slate-700/50 rounded-2xl p-6 shadow-lg">
            <RatingProgressBar kqRating={kqRating} questions={questions} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 w-full">
            {questions.map((ratingQue, idx) => {
              const currentScore = (
                avgRatingsByQuestionId(kqRating, ratingQue._id) / 2
              ).toFixed(1);

              return (
                <div
                  key={idx}
                  className="bg-[#111827] border border-slate-700/50 rounded-2xl p-5 shadow-lg group"
                >
                  <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 justify-between">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold shadow-inner flex-shrink-0 ${visualRating(
                        parseFloat(currentScore),
                      )} text-white`}
                    >
                      {currentScore}
                    </div>

                    <div className="flex-1 text-center sm:text-left">
                      <label className="text-slate-200 font-semibold mb-1 block">
                        {ratingQue.question}
                      </label>
                    </div>

                    <div className="flex-shrink-0 scale-90 sm:scale-100 origin-right">
                      <Rating
                        initialValue={parseFloat(currentScore)}
                        allowFraction
                        readonly
                        emptyIcon={<StarsEmpty className="inline-block" />}
                        fillIcon={
                          <StarsFilled className="inline-block drop-shadow-md" />
                        }
                        SVGclassName="inline-block"
                        SVGstorkeWidth={0}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-[#111827] rounded-3xl p-8 border border-slate-800 shadow-lg mt-10">
            <div className="flex flex-col items-center justify-center space-y-4">
              <label className="text-xl font-bold text-slate-100 block mb-2">
                Recommended
              </label>

              <div className="w-full max-w-lg bg-[#081325] rounded-full h-8 border border-slate-700 shadow-inner overflow-hidden relative">
                <div
                  className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 transition-all duration-1000 ease-out rounded-full relative flex items-center shadow-[0_0_15px_rgba(52,211,153,0.5)] border-r border-emerald-300/50"
                  style={{
                    width: `${(getAvgRecommendBoolean(kqRating) * 100).toFixed(1)}%`,
                  }}
                >
                  {/* Subtle inner shine */}
                  <div className="absolute top-0 left-0 right-0 h-1/2 bg-white/20 rounded-t-full"></div>
                </div>
                {/* Text centered perfectly over the bar completely decoupled from width constraint */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-bold text-white drop-shadow-md text-sm">
                    {(getAvgRecommendBoolean(kqRating) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <Circles />
      )}
    </>
  );
}
